import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import './ExploreCoursesPage.css';

const DEFAULT_LANGUAGES = [
  { code: 'en', flag: '🇬🇧', label: 'English' },
  { code: 'fr', flag: '🇫🇷', label: 'French' },
  { code: 'es', flag: '🇪🇸', label: 'Spanish' },
  { code: 'de', flag: '🇩🇪', label: 'German' },
  { code: 'it', flag: '🇮🇹', label: 'Italian' }
];

function getModulesForLanguage(langCode) {
  const isAlgerianForeignLang =
    langCode === 'es' || langCode === 'de' || langCode === 'it';

  const educationalDescription = isAlgerianForeignLang
    ? 'Vocabulary for 2nd year and 3rd year high school, aligned with the Algerian curriculum for this language.'
    : 'Vocabulary by school level, structured & progressive.';

  return [
    {
      id: 'edu-levels-vocab',
      icon: '📚',
      title: "Educational levels' vocabulary",
      description: educationalDescription,
      badge: { type: 'progress', value: 35 }
    },
    {
      id: 'international-exams-vocab',
      icon: '🌍',
      title: "International exam's vocabulary",
      description: 'IELTS/TOEFL/DELF-style high-frequency exam words.',
      badge: { type: 'new' }
    },
    {
      id: 'general-vocab',
      icon: '📖',
      title: 'General vocabulary',
      description: 'Everyday topics: travel, work, hobbies, and more.',
      badge: { type: 'progress', value: 62 }
    },
    {
      id: 'academic-vocab-esp',
      icon: '🎓',
      title: 'Academic vocabulary (ESP)',
      description: 'Academic and domain-specific vocabulary (ESP).',
      badge: { type: 'progress', value: 10 }
    },
    {
      id: 'dictation',
      icon: '🎧',
      title: 'Dictation',
      description: 'Train listening accuracy with graded dictations.',
      badge: { type: 'new' }
    },
    {
      id: 'reading',
      icon: '📖',
      title: 'Reading',
      description: 'Short passages to build comprehension and speed.',
      badge: { type: 'progress', value: 0 }
    },
    {
      id: 'placement-test',
      icon: '📊',
      title: 'Vocabulary placement test',
      description: 'Quick placement to suggest the best starting point.',
      badge: { type: 'new' }
    },
    {
      id: 'virtual-classroom',
      icon: '🏫',
      title: 'Virtual classroom',
      description: 'Join live sessions, assignments, and teacher feedback.',
      badge: { type: 'progress', value: 80 }
    }
  ];
}

function clampPercent(n) {
  const num = Number(n);
  if (Number.isNaN(num)) return 0;
  return Math.max(0, Math.min(100, Math.round(num)));
}

function ModuleBadge({ badge }) {
  if (!badge) return null;

  if (badge.type === 'new') {
    return <span className="ec-badge ec-badge--new">New</span>;
  }

  if (badge.type === 'progress') {
    const value = clampPercent(badge.value);
    return (
      <span className="ec-badge ec-badge--progress" title={`${value}% complete`}>
        {value}%
      </span>
    );
  }

  return null;
}

function ModuleProgressBar({ badge }) {
  if (!badge || badge.type !== 'progress') return null;
  const value = clampPercent(badge.value);
  return (
    <div className="ec-progress" aria-label={`Progress ${value}%`}>
      <div className="ec-progress__bar" style={{ width: `${value}%` }} />
    </div>
  );
}

function ModuleCard({ module }) {
  return (
    <button type="button" className="ec-module" onClick={() => {}}>
      <div className="ec-module__top">
        <div className="ec-module__icon" aria-hidden="true">
          {module.icon}
        </div>
        <div className="ec-module__meta">
          <div className="ec-module__titleRow">
            <div className="ec-module__title">{module.title}</div>
            <ModuleBadge badge={module.badge} />
          </div>
          <div className="ec-module__desc">{module.description}</div>
        </div>
      </div>
      <ModuleProgressBar badge={module.badge} />
    </button>
  );
}

function AnimatedPanel({ isOpen, children }) {
  const innerRef = useRef(null);
  const [maxHeight, setMaxHeight] = useState(0);

  useEffect(() => {
    if (!innerRef.current) return;

    if (isOpen) {
      const next = innerRef.current.scrollHeight;
      setMaxHeight(next);
    } else {
      setMaxHeight(0);
    }
  }, [isOpen, children]);

  return (
    <div
      className={`ec-panel ${isOpen ? 'ec-panel--open' : ''}`}
      style={{ maxHeight: isOpen ? `${maxHeight}px` : '0px' }}
    >
      <div ref={innerRef} className="ec-panel__inner">
        {children}
      </div>
    </div>
  );
}

export default function ExploreCoursesPage() {
  const [searchParams] = useSearchParams();
  const langFromUrl = searchParams.get('lang');
  const languages = useMemo(() => DEFAULT_LANGUAGES, []);
  const modules = useMemo(() => getModulesForLanguage(langFromUrl), [langFromUrl]);

  const activeLanguage = useMemo(
    () => (langFromUrl ? languages.find((l) => l.code === langFromUrl) : null),
    [langFromUrl, languages]
  );

  const openModulesInNewTab = (langCode) => {
    const url = `${window.location.origin}${window.location.pathname}?lang=${encodeURIComponent(langCode)}`;
    window.open(url, '_blank');
  };

  // With ?lang=: show "Change language" + "Modules for ..." + modules only
  if (langFromUrl) {
    return (
      <div className="ec-page">
        <div className="ec-container">
          <div className="ec-header ec-header--withBack">
            <Link to="/user-dashboard" className="ec-backBtn" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginBottom: 12 }}>
              <span className="material-icons" style={{ fontSize: 20 }}>arrow_back</span>
              <span>Change language</span>
            </Link>
            <div className="ec-selected ec-selected--header">
              <div className="ec-selected__label">
                Modules for{' '}
                <span className="ec-selected__lang">
                  {activeLanguage?.flag} {activeLanguage?.label}
                </span>
              </div>
            </div>
          </div>
          <div className="ec-grid" role="region" aria-label="Modules">
            {modules.map((m) => (
              <ModuleCard key={m.id} module={m} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  // No lang: show only language picker; clicking opens modules in new tab
  return (
    <div className="ec-page">
      <div className="ec-container">
        <div className="ec-header">
          <div>
            <h1 className="ec-title">Explore Courses</h1>
            <p className="ec-subtitle">
              Choose a language to see the full learning modules in a new tab.
            </p>
          </div>
        </div>

        <div className="ec-languageRow" role="tablist" aria-label="Languages">
          {languages.map((lang) => (
            <button
              key={lang.code}
              type="button"
              className="ec-lang"
              onClick={() => openModulesInNewTab(lang.code)}
              role="tab"
            >
              <span className="ec-lang__flag" aria-hidden="true">
                {lang.flag}
              </span>
              <span className="ec-lang__label">{lang.label}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

